/**
 * Popup Modal Custom Element
 *
 * One element per configured popup. Uses native <dialog> for focus trap, backdrop and
 * Escape handling.
 *
 * Qualification and triggering are independent, so all four combinations work from one
 * implementation: show to everyone or only to matching UTM parameters, on page load or
 * on exit intent.
 *
 * Generalized from the previous utm-modal.js. The Section Rendering API shell/fetch
 * machinery is still here, but now only for popups whose content resolves per page:
 * a metafield-scoped popup can be overridden per page via utm_modal_metaobject, so a
 * later page (the cart page after a redirect, say) has to re-fetch the activation page
 * to know what to display. Popups scoped to all pages render identical content
 * everywhere and never fetch.
 **/

/** Qualified campaigns and session-scoped dismissals. Cleared when the tab closes. */
const SESSION_KEY = 'popup_modal_session';

/** Dismissals with a multi-day cap. Survives across sessions. */
const PERSISTED_KEY = 'popup_modal_dismissed';

/**
 * How close to the top of the page counts as having scrolled back to the top, in px.
 *
 * This replaced an upward-velocity threshold, which was unusable: a casual mobile swipe
 * is around 1200px/s and iOS momentum scrolling exceeds 3000px/s, so any threshold low
 * enough to catch a deliberate flick also catches ordinary scrolling. Arriving back at
 * the top is a stable, observable event instead of a jittery per-frame derivative.
 */
const SCROLL_UP_TOP_THRESHOLD_PX = 100;

const DAY_IN_MS = 86400000;

/**
 * @param {string} key
 * @param {Storage} storage
 * @returns {object}
 */
function readStore(key, storage) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};

    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch (error) {
    return {};
  }
}

function writeStore(key, storage, data) {
  try {
    storage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // Private browsing or storage full. Popups simply won't be capped.
  }
}

/**
 * Records that a popup qualified, so it keeps showing after the visitor navigates away
 * from the landing page and the UTM parameters are no longer in the URL.
 *
 * Keyed by popup instance rather than content handle, and stores the activation path:
 * content can be page-specific (via the utm_modal_metaobject override), so a later page
 * may need to re-fetch this path to know what to display.
 *
 * @param {string} instanceKey
 * @param {{ path: string, handle: string }} activation
 */
function markQualified(instanceKey, activation) {
  const session = readStore(SESSION_KEY, sessionStorage);
  session.active = { ...(session.active || {}), [instanceKey]: activation };
  writeStore(SESSION_KEY, sessionStorage, session);
}

/**
 * @param {string} instanceKey
 * @returns {{ path: string, handle: string } | null}
 */
function getQualified(instanceKey) {
  const active = readStore(SESSION_KEY, sessionStorage).active;
  const entry = active && active[instanceKey];

  return entry && entry.path && entry.handle ? entry : null;
}

/**
 * @param {string} instanceKey
 */
function clearQualified(instanceKey) {
  const session = readStore(SESSION_KEY, sessionStorage);
  if (!session.active || !session.active[instanceKey]) return;

  delete session.active[instanceKey];
  writeStore(SESSION_KEY, sessionStorage, session);
}

/**
 * @param {string} activationPath
 * @param {string} sectionId
 * @returns {string}
 */
function buildSectionFetchUrl(activationPath, sectionId) {
  const rootRaw = window.Shopify?.routes?.root || '/';
  const root = rootRaw.replace(/\/+$/, '') || '';
  const path = activationPath.startsWith('/') ? activationPath : `/${activationPath}`;

  return `${root}${path}?${new URLSearchParams({ sections: sectionId }).toString()}`;
}

/**
 * @param {string} handle
 * @param {number} capDays - 0 records a session-scoped dismissal.
 */
function recordDismissal(handle, capDays) {
  if (capDays > 0) {
    const dismissed = readStore(PERSISTED_KEY, localStorage);
    dismissed[handle] = Date.now();
    writeStore(PERSISTED_KEY, localStorage, dismissed);
    return;
  }

  const session = readStore(SESSION_KEY, sessionStorage);
  const list = Array.isArray(session.dismissed) ? session.dismissed : [];
  session.dismissed = [...new Set([...list, handle])];
  writeStore(SESSION_KEY, sessionStorage, session);
}

/**
 * @param {string} handle
 * @param {number} capDays
 * @returns {boolean}
 */
function isDismissed(handle, capDays) {
  if (capDays > 0) {
    const dismissedAt = Number(readStore(PERSISTED_KEY, localStorage)[handle]);
    if (!Number.isFinite(dismissedAt)) return false;

    return (Date.now() - dismissedAt) / DAY_IN_MS < capDays;
  }

  const session = readStore(SESSION_KEY, sessionStorage);
  return Array.isArray(session.dismissed) && session.dismissed.includes(handle);
}

class PopupModal extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.hasShown = false;
    this.maxScrollPercent = 0;
    this.idleTimer = null;
    this.lastScrollY = 0;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) {
      console.error('Popup Modal: No <dialog> element found');
      return;
    }

    this.handle = this.dataset.modalHandle || '';
    this.isShell = this.dataset.shell === 'true';
    this.fetchSection = this.dataset.fetchSection || 'popup-modal';
    this.audience = this.dataset.audience || 'all';
    this.pageQualifies = this.dataset.pageQualifies === 'true';
    this.trigger = this.dataset.trigger || 'immediate';
    this.capDays = Number(this.dataset.capDays) || 0;
    this.scrollThreshold = Number(this.dataset.scrollThreshold) || 0;
    this.mobileTrigger = this.dataset.mobileTrigger || 'off';
    this.mobileIdleMs = Number(this.dataset.mobileIdleMs) || 0;
    this.debugMode = this.dataset.debugMode === 'true';
    this.designMode = this.dataset.designMode === 'true';
    this.dispatchesPriority = this.hasAttribute('data-priority-modal');

    this.bindCloseHandlers();

    void this.init();
  }

  async init() {
    /* Prevent the pop-up firing in the theme customizer outside of debug mode */
    if (this.designMode && !this.debugMode) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    // A shell has no content of its own: it only matters if this popup already qualified
    // on an earlier page, whose markup it re-fetches.
    if (this.isShell) {
      const activation = getQualified(this.id);

      if (!activation) {
        this.dispatchPriorityResolved('no-match');
        return;
      }

      if (!this.debugMode && isDismissed(activation.handle, this.capDays)) {
        this.dispatchPriorityResolved('already-shown');
        return;
      }

      if (!(await this.fetchAndInject(activation))) {
        this.dispatchPriorityResolved('no-match');
        return;
      }

      this.armTrigger();
      return;
    }

    if (!this.handle) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    if (!this.debugMode && isDismissed(this.handle, this.capDays)) {
      this.dispatchPriorityResolved('already-shown');
      return;
    }

    if (!this.qualifies()) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    this.armTrigger();
  }

  armTrigger() {
    if (this.trigger === 'exit_intent') {
      this.armExitIntent();
      return;
    }

    this.show('immediate');
  }

  /**
   * Pulls this popup's counterpart out of the activation page's rendered section and
   * moves its dialog contents into ours. Located by element id, which is stable across
   * pages because it derives from the section or block id.
   *
   * @param {{ path: string, handle: string }} activation
   * @returns {Promise<boolean>}
   */
  async fetchAndInject(activation) {
    try {
      const response = await fetch(buildSectionFetchUrl(activation.path, this.fetchSection));
      if (!response.ok) return false;

      const html = (await response.json())[this.fetchSection];
      if (!html || typeof html !== 'string') return false;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedHost =
        doc.querySelector(`popup-modal[id="${this.id}"]`) || doc.querySelector('popup-modal');
      if (!fetchedHost) return false;

      const fetchedDialog =
        fetchedHost.querySelector('dialog.popup-modal__dialog') ||
        fetchedHost.querySelector('dialog');
      if (!fetchedDialog || !fetchedDialog.firstChild) return false;

      // Content is page-resolved, so the handle can differ from ours. Adopt it: the
      // dismissal cap keys off the content actually shown.
      const fetchedHandle = fetchedHost.getAttribute('data-modal-handle');
      this.handle = fetchedHandle || activation.handle;

      /*
        `class` and `style` carry per-entry presentation set on the dialog itself — the
        max-width and image-split custom properties. Without copying them, a shell (which
        renders with no content and therefore no style) would show fetched content at the
        default dimensions instead of the ones that entry configured.
      */
      for (const attribute of ['aria-label', 'aria-labelledby', 'class', 'style']) {
        const value = fetchedDialog.getAttribute(attribute);
        if (value) this.dialog.setAttribute(attribute, value);
      }

      this.dialog.innerHTML = '';
      while (fetchedDialog.firstChild) {
        this.dialog.appendChild(fetchedDialog.firstChild);
      }

      return true;
    } catch (error) {
      console.error('Popup Modal: Section fetch failed', error);
      return false;
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    if (this.idleTimer) clearTimeout(this.idleTimer);

    /* Release the scroll lock if the element is removed while the dialog is still open. */
    if (this.dialog?.open) {
      document.body.style.overflow = '';
    }
  }

  bindCloseHandlers() {
    this.dialog.addEventListener('click', (event) => {
      const target = event.target;

      // The CTA may be a real link. Record the dismissal before it navigates so the
      // offer doesn't reappear after the visitor engages with it.
      if (target.closest && target.closest('.popup-modal__button')) {
        if (!target.closest('[id^="ModalCloseCTA-"]')) {
          recordDismissal(this.handle, this.capDays);
          clearQualified(this.id);
          return;
        }
      }

      // Prefix matching rather than full ids: already scoped to this dialog, and it
      // avoids escaping issues if a metaobject handle ever starts with a digit.
      const closer =
        target.closest &&
        (target.closest('[id^="ModalClose-"]') ||
          target.closest('[id^="ModalCloseCTA-"]') ||
          target.closest('[id^="ModalCloseLink-"]'));

      if (closer || target === this.dialog) {
        this.hide();
      }
    });

    // Escape fires `cancel` natively, but our cleanup needs to run.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
  }

  /**
   * @returns {boolean} Whether this popup is allowed to show for this visitor.
   */
  qualifies() {
    if (this.audience !== 'utm') {
      return this.pageQualifies;
    }

    // A campaign that matched earlier this session keeps qualifying, so it survives
    // navigation away from the landing page where the parameters were present.
    if (getQualified(this.id)) return true;

    if (!this.pageQualifies) return false;

    if (!this.hasMatchingUTMParameter()) return false;

    markQualified(this.id, { path: window.location.pathname, handle: this.handle });
    return true;
  }

  /**
   * @returns {object} URL parameters, values lowercased for comparison.
   */
  getURLParameters() {
    const params = {};

    for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
      params[key] = value.toLowerCase();
    }

    return params;
  }

  /**
   * Checks the URL against every `data-utm-*` attribute on this element. Each attribute
   * holds a comma-separated list of accepted values.
   *
   * @returns {boolean}
   */
  hasMatchingUTMParameter() {
    const urlParams = this.getURLParameters();

    for (const key in this.dataset) {
      if (!key.startsWith('utm')) continue;

      const configuredValues = this.dataset[key];
      if (!configuredValues) continue;

      // utmSource -> utm_source
      const paramName = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      const urlValue = urlParams[paramName];
      if (!urlValue) continue;

      const accepted = configuredValues
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0);

      if (accepted.includes(urlValue)) return true;
    }

    return false;
  }

  armExitIntent() {
    const { signal } = this.abortController;

    /*
      Both platforms need the scroll listener: touch uses it for the scroll-up trigger,
      and desktop needs maxScrollPercent for the scroll gate. So the mobile triggers have
      to check isTouch themselves — being on the touch branch here isn't enough.
    */
    this.isTouch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Seed from the current position: a page can load already scrolled (back navigation
    // restores it), and no scroll event fires until the visitor moves.
    this.maxScrollPercent = this.getScrollPercent();

    window.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });

    if (this.isTouch) {
      this.armMobileTrigger(signal);
      return;
    }

    /*
      Register both leave events rather than sniffing the browser. `mouseleave` on document
      covers Chromium and Firefox; the older window `mouseout` with a null relatedTarget is
      kept for Safari, which has historically been inconsistent about the former.
      `hasShown` dedupes when both fire.
    */
    document.addEventListener(
      'mouseleave',
      (event) => this.handleLeaveIntent(event, 'desktop-mouseleave'),
      { signal }
    );

    window.addEventListener(
      'mouseout',
      (event) => {
        if (event.relatedTarget) return;
        this.handleLeaveIntent(event, 'desktop-mouseout');
      },
      { signal }
    );
  }

  /**
   * @param {MouseEvent} event
   * @param {string} reason
   */
  handleLeaveIntent(event, reason) {
    // Left via the top of the window, not the sides or bottom.
    if (event.clientY > 0) return;

    this.attemptExitOpen(reason);
  }

  armMobileTrigger(signal) {
    // Only the idle timer needs arming here. 'scroll-up' rides the scroll listener in
    // handleScroll(), which gates on isTouch; 'off' adds no mobile trigger at all.
    if (this.mobileTrigger !== 'idle' || this.mobileIdleMs <= 0) return;

    const resetIdleTimer = () => {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(
        () => this.attemptExitOpen('mobile-idle'),
        this.mobileIdleMs
      );
    };

    for (const eventName of ['touchstart', 'touchmove', 'click', 'scroll']) {
      document.addEventListener(eventName, resetIdleTimer, { passive: true, signal });
    }

    resetIdleTimer();
  }

  /** @returns {number} How far down the page the visitor currently is, as a percent. */
  getScrollPercent() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;

    return scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
  }

  /**
   * The engagement gate. Kept separate from the raw percentage because a page with nothing
   * to scroll would otherwise make the threshold permanently unsatisfiable: no scroll
   * event ever fires, so maxScrollPercent stays at 0 and exit intent can never open.
   *
   * Measured at trigger time rather than at arm time, so content that loads in later — or
   * an accordion opening — is taken into account.
   *
   * @returns {boolean}
   */
  hasEngaged() {
    // Nothing to scroll means the visitor has already seen the whole page.
    if (document.documentElement.scrollHeight <= window.innerHeight) return true;

    return this.maxScrollPercent >= this.scrollThreshold;
  }

  handleScroll() {
    const scrollY = window.scrollY;

    this.maxScrollPercent = Math.max(this.maxScrollPercent, this.getScrollPercent());

    // Touch only. Without the isTouch check this fires on desktop, where the real
    // trigger is the pointer leaving the top of the window.
    if (!this.isTouch || this.mobileTrigger !== 'scroll-up') return;

    /*
      Fires on arriving back at the top of the page while moving upward. The scroll gate
      in attemptExitOpen separately requires the visitor to have scrolled meaningfully
      down first, so this reads as "went down the page, then came all the way back"
    */
    const isMovingUp = scrollY < this.lastScrollY;

    if (isMovingUp && scrollY <= SCROLL_UP_TOP_THRESHOLD_PX) {
      this.attemptExitOpen('mobile-scroll-up');
    }

    this.lastScrollY = scrollY;
  }

  /**
   * @param {string} reason - Which signal fired, passed through for analytics.
   */
  attemptExitOpen(reason) {
    if (this.hasShown) return;

    // Don't interrupt someone who hasn't engaged with the page yet.
    if (!this.debugMode && !this.hasEngaged()) return;

    // Never stack on an already-open dialog, e.g. an on-load popup still showing.
    if (document.querySelector('dialog[open]')) return;

    this.abortController.abort();
    if (this.idleTimer) clearTimeout(this.idleTimer);

    this.show(reason);
  }

  /**
   * @param {string} reason
   */
  show(reason) {
    if (this.hasShown) return;

    /*
      Never stack dialogs. Exit intent popups re-check on the next signal (see
      attemptExitOpen, which returns before tearing down its listeners), but an on-load
      popup that loses the race just waits for the next page load. No dismissal is recorded,
      so nothing is lost. Priority still resolves so anything waiting on it isn't left hanging.
    */
    if (document.querySelector('dialog[open]')) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    this.hasShown = true;

    try {
      this.dialog.showModal();
    } catch (error) {
      console.error('Popup Modal: Failed to show modal', error);
      return;
    }

    // Drop the auto-focus outline; focus returns on Tab.
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    // Native dialog doesn't block background scroll on all mobile browsers.
    document.body.style.overflow = 'hidden';

    if (window.pauseAllMedia) window.pauseAllMedia();

    this.dispatchEvent(
      new CustomEvent('popup-modal:shown', {
        bubbles: true,
        detail: { handle: this.handle, trigger: this.trigger, reason }
      })
    );
  }

  hide() {
    if (!this.dialog) return;

    this.dialog.close();
    document.body.style.overflow = '';

    if (window.pauseAllMedia) window.pauseAllMedia();

    // Clear the activation too, so it stops following the visitor across pages.
    recordDismissal(this.handle, this.capDays);
    clearQualified(this.id);

    this.dispatchEvent(
      new CustomEvent('popup-modal:dismissed', {
        bubbles: true,
        detail: { handle: this.handle }
      })
    );

    this.dispatchPriorityResolved('closed');
  }

  /**
   * Releases popups that wait on this one.  Only on-load popups dispatch: an exit
   * intent popup resolves as soon as the page loads and would release it far too early.
   *
   * @param {string} reason - 'closed' | 'no-match' | 'already-shown'
   */
  dispatchPriorityResolved(reason) {
    if (!this.dispatchesPriority) return;

    document.dispatchEvent(
      new CustomEvent('priority-modal-resolved', {
        bubbles: true,
        detail: { modalId: this.id, reason }
      })
    );
  }
}

if (!customElements.get('popup-modal')) {
  customElements.define('popup-modal', PopupModal);
}
